import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAppTracking from 'hooks/useAppTracking';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import DriverForm from 'pages/OTOFlexMain/Drivers/DriverForm';
import BrandForm from 'pages/Settings/Brands/BrandForm';
import UserForm from 'pages/Users/UserForm';

import { AUTOCOMPLETE } from 'services/consts';

import PATHS from 'utils/paths';

import { faEye } from '@fortawesome/pro-solid-svg-icons';

export const BranchContactInfo = ({ mode }) => {
	const defaultColSpan = {
		md: 6
	};
	const viewMode = mode === 'view';
	const isAuthorized = useAccessControl();
	const isRestricted = useRestriction();
	const { track } = useAppTracking();
	const navigate = useNavigate();

	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const userAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'name', width: 200 },
			{ accessor: 'email', width: 150 }
		],
		[]
	);

	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isLoading} required>
				<StringInput disabled={viewMode} name="person" />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="senderEmail" isLoading={isLoading} required>
				<StringInput disabled={viewMode} name="email" />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading} required>
				<PhoneNumber
					disabled={viewMode}
					onChange={(value) => {
						setValue('phone', value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch('phone')}
					name="phone"
				/>
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="vatNumber" isLoading={isLoading}>
				<StringInput name="vatNumber" disabled={viewMode} />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="servingRadiusKM" isLoading={isLoading} required>
				<IntegerInput disabled={viewMode} unit="km" name="servingRadius" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="storeName" isLoading={isLoading} roleName="BrandingModule">
				<AutoComplete
					name="storeID"
					disabled={viewMode}
					autoCompleteName={AUTOCOMPLETE.BRANDS}
					value={watch(`storeID`)}
					setValue={(value) => {
						setValue(`storeID`, value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
					}}
					firstItem={
						isAuthorized('/autocomplete/brand/addANewBrand')
							? {
									action: () =>
										NiceModal.show(BrandForm, {
											setBrand: (value) => {
												setValue(`storeID`, value, {
													shouldValidate: true,
													shouldDirty: true
												});
											}
										}),
									label: <Typography color="var(--blue)">addANewBrand</Typography>,
									disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
									disabledTooltip: (
										<div style={{ display: 'block' }}>
											<ModuleToolTipContainer
												infoImage="lottie_multiBrand"
												title="multiBrandModule"
												description="multiBrandModuleSubtitle"
											/>

											<Button
												style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
												className="mt-3"
												label="showPlans"
												icon={faEye}
												variant="blue"
												onMouseDown={() => {
													track('moreThan3Brands');
													navigate(PATHS.OTO_MODULES);
												}}
											/>
										</div>
									)
								}
							: undefined
					}
					columns={brandAutocompleteColumns}
					selectionLabelAccessor={['Name']}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="createShipmentAuto" isLoading={isLoading}>
				<SwitchInput className="mt-3" formMode viewMode={viewMode} name="autoShipment" />
			</FormItem>

			<FormItem label="accessibleUsers" isLoading={isLoading}>
				<AutoComplete
					name="branchUsers"
					disabled={viewMode}
					autoCompleteName={AUTOCOMPLETE.BRANCH_USERS}
					value={watch('branchUsers')}
					setValue={(value) => {
						setValue('branchUsers', value?.map((item) => item?.ID) ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					firstItem={{
						action: () =>
							NiceModal.show(UserForm, {
								setUser: (value) => {
									setValue(`branchUsers`, [...(watch('branchUsers') ?? []), value], {
										shouldValidate: true,
										shouldDirty: true
									});
								},
								userType: 'branchUser'
							}),
						label: <Typography color="var(--blue)">addANewBranchUser</Typography>
					}}
					columns={userAutocompleteColumns}
					selectionLabelAccessor={['name']}
					multipleSelection
				/>
			</FormItem>

			<FormItem label="otoFlexDrivers" isLoading={isLoading} roleName="OTOFlexModule">
				<AutoComplete
					name="driverUsers"
					disabled={viewMode}
					autoCompleteName={AUTOCOMPLETE.DRIVER_USERS}
					value={watch('driverUsers')}
					setValue={(value) => {
						setValue('driverUsers', value?.map((item) => item?.ID) ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					firstItem={{
						action: () =>
							NiceModal.show(DriverForm, {
								setDriver: (value) => {
									setValue(`driverUsers`, [...(watch('driverUsers') ?? []), value], {
										shouldValidate: true,
										shouldDirty: true
									});
								}
							}),
						label: <Typography color="var(--blue)">addANewDriverUser</Typography>
					}}
					columns={userAutocompleteColumns}
					selectionLabelAccessor={['name']}
					multipleSelection
				/>
			</FormItem>
		</>
	);
};
